import { useMemo } from 'react';
import { Medal, Megaphone, Rocket } from 'lucide-react';
import { Card } from '../ui/card';
import { SkeletonText } from '../ui/skeleton';
import { usd } from '../../lib/graph-visuals';
import { validatedRecs } from '../../lib/savings';
import { useOrgDomainRecs } from './useOrgDomainRecs';

const MEDAL_COLORS = ['text-warning', 'text-muted', 'text-haze'];
const WINDOW_DAYS = 30;

interface Shout {
  name: string;
  saving: number;
  /** The domain where this person banked the most, for attribution. */
  topEnv: string;
}

/**
 * Shout-outs (gamification): the top 3 people who banked the most proven monthly
 * savings across the org in the last 30 days, credited to the domain where
 * they saved the most. A little recognition for the teammates doing the work.
 */
export function ShoutOuts() {
  const { envs, loaded } = useOrgDomainRecs();

  const top = useMemo(() => {
    const since = new Date();
    since.setDate(since.getDate() - WINDOW_DAYS);
    // person -> { total, perEnv: env -> saving }
    const byPerson = new Map<string, { total: number; perEnv: Map<string, number> }>();
    for (const env of envs) {
      for (const r of validatedRecs(env.recs)) {
        if (new Date(r.validatedAt!) < since) continue;
        const saving = r.realizedMonthlySaving ?? 0;
        if (saving <= 0) continue;
        const name = r.executedByName ?? 'Unknown';
        const entry = byPerson.get(name) ?? { total: 0, perEnv: new Map() };
        entry.total += saving;
        entry.perEnv.set(env.name, (entry.perEnv.get(env.name) ?? 0) + saving);
        byPerson.set(name, entry);
      }
    }
    return [...byPerson.entries()]
      .map<Shout>(([name, e]) => ({
        name,
        saving: e.total,
        topEnv: [...e.perEnv.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '',
      }))
      .sort((a, b) => b.saving - a.saving)
      .slice(0, 3);
  }, [envs]);

  return (
    <Card className="p-5">
      <div className="mb-1 flex items-center gap-2">
        <Megaphone aria-hidden className="h-4 w-4" />
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
          Shout-outs
        </h2>
      </div>
      <p className="mb-3 text-xs text-muted">Top savers across the org in the last 30 days.</p>

      {!loaded ? (
        <SkeletonText lines={3} lineClassName="h-4" />
      ) : top.length === 0 ? (
        <p className="text-sm text-muted">
          No savings validated in the last 30 days yet — be the first on the board.{' '}
          <Rocket aria-hidden className="inline h-4 w-4 align-text-bottom" />
        </p>
      ) : (
        <ol className="space-y-2">
          {top.map((s, i) => (
            <li
              key={s.name}
              className="flex items-center gap-3 rounded-md border border-hairline bg-surface-2 px-3 py-2"
            >
              <Medal aria-hidden className={`h-5 w-5 ${MEDAL_COLORS[i]}`} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-bone">{s.name}</div>
                {s.topEnv && <div className="truncate text-xs text-muted">in {s.topEnv}</div>}
              </div>
              <span className="font-display font-bold text-iris tnum">
                {usd(s.saving)}
                <span className="text-[0.6rem] font-normal text-muted">/mo</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
